/**
 * <TutorCropFrame> — une photo et la zone que le tuteur en garde (molecule).
 *
 * Le dessin du recadrage de `ImageCropModal.web.js` : la photo entière, la zone retenue encadrée
 * à la couleur de l'univers avec ses quatre poignées, et le reste voilé.
 *
 * Le geste, avec `onChangeArea` : glisser la zone la déplace, tirer une poignée la redimensionne depuis
 * ce coin, au doigt comme à la souris — le répondeur de react-native (`PanResponder`), le même sur le
 * web, iOS et Android, plutôt que `react-image-crop` du legacy (une dépendance d'interface tierce) ; la
 * poignée, plus profonde, prend le geste avant la zone. Contrôlé : la zone arrive en props
 * (`area`, en fractions de la photo) et revient à l'écran, qui la tient, quand le doigt ou la souris se lève ;
 * pendant le geste, le cadre suit seul (un état d'interface), sans redessiner l'écran à chaque mouvement. Les règles de la
 * zone (rester dans la photo, une taille minimale) sont dans `cropAreaFormatter.ts`. Sans `onChangeArea`, le cadre
 * est un dessin fixe.
 */
import React, { useMemo, useRef, useState } from "react"
import {
    PanResponder,
    Platform,
    useWindowDimensions,
    View,
    type LayoutChangeEvent,
    type PanResponderInstance,
} from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { FittedImage } from "../../atoms/FittedImage"
import { type UITutorCropArea } from "../../../ports/primary/ui/types/tutorEleves"
import {
    clamp,
    moveCropArea,
    resizeCropArea,
    type CropCorner,
} from "../../../ports/primary/ui/formatters/cropAreaFormatter"

/** La photo ne s'étale pas plus large : une page en portrait tient encore à l'écran d'un ordinateur. */
const CROP_MAX_WIDTH = 520

/**
 * La hauteur que la photo laisse à la feuille (son titre, la consigne, le pied) : la photo entière et ses quatre
 * coins tiennent à l'écran, comme l'ancien (`maxHeight: calc(100vh - 220px)`) une fois la feuille comptée.
 */
const CROP_SHEET_RESERVE = 280

/** Le côté d'une poignée. */
const HANDLE = 14

/** Le côté de la zone où le geste prend une poignée, centrée sur son coin : 14 px ne se visent pas au doigt. */
const HANDLE_HIT = 36

export type TutorCropFrameProps = {
    uri: string
    area: UITutorCropArea
    /** La zone à la fin d'un geste. Absent : pas de geste, le cadre est fixe. */
    onChangeArea?: (area: UITutorCropArea) => void
    testID?: string
}

const CORNERS: CropCorner[] = ["topLeft", "topRight", "bottomLeft", "bottomRight"]

/** Une boîte posée en fractions de la boîte de la photo : ses bords gauche, haut, droit, bas. */
const box = (left: number, top: number, right: number, bottom: number) => ({
    position: "absolute" as const,
    left: `${left * 100}%` as const,
    top: `${top * 100}%` as const,
    width: `${Math.max(0, right - left) * 100}%` as const,
    height: `${Math.max(0, bottom - top) * 100}%` as const,
})

export function TutorCropFrame(props: TutorCropFrameProps) {
    const { uri, area, onChangeArea, testID } = props
    // La zone que suit le geste en cours, rendue à l'écran au lâcher ; `null` hors d'un geste.
    const [dragArea, setDragArea] = useState<UITutorCropArea | null>(null)
    // La boîte mesurée, la zone au début du geste (chaque mouvement part d'elle), la zone du dernier mouvement et
    // le rappel du moment : des refs, que les répondeurs, créés une fois, lisent à chaque mouvement.
    const sizeRef = useRef({ width: 0, height: 0 })
    const startRef = useRef(area)
    const draggedRef = useRef<UITutorCropArea | null>(null)
    const areaRef = useRef(area)
    areaRef.current = area
    const changeRef = useRef(onChangeArea)
    changeRef.current = onChangeArea
    const interactive = onChangeArea != null
    // Une photo en portrait ne dépasse pas la fenêtre : sa largeur suit la hauteur que la feuille lui laisse.
    const [ratio, setRatio] = useState<number | null>(null)
    const { height: windowHeight } = useWindowDimensions()
    const maxWidth =
        ratio != null
            ? Math.min(CROP_MAX_WIDTH, Math.max(160, (windowHeight - CROP_SHEET_RESERVE) * ratio))
            : CROP_MAX_WIDTH

    const onLayout = (event: LayoutChangeEvent) => {
        const { width, height } = event.nativeEvent.layout
        sizeRef.current = { width, height }
    }
    const responders = useMemo(() => {
        const fractions = (dx: number, dy: number) => ({
            dx: sizeRef.current.width > 0 ? dx / sizeRef.current.width : 0,
            dy: sizeRef.current.height > 0 ? dy / sizeRef.current.height : 0,
        })
        const release = () => {
            const dragged = draggedRef.current
            draggedRef.current = null
            setDragArea(null)
            if (dragged != null) changeRef.current?.(dragged)
        }
        // Les répondeurs ne sont posés que quand le cadre a un geste (`interactive`) : ils prennent toujours.
        const responder = (next: (start: UITutorCropArea, dx: number, dy: number) => UITutorCropArea) =>
            PanResponder.create({
                onStartShouldSetPanResponder: () => true,
                onMoveShouldSetPanResponder: () => true,
                // Le geste commencé ne se laisse pas prendre par un défilement de la feuille.
                onPanResponderTerminationRequest: () => false,
                onPanResponderGrant: () => {
                    startRef.current = areaRef.current
                },
                onPanResponderMove: (_event, gesture) => {
                    const { dx, dy } = fractions(gesture.dx, gesture.dy)
                    draggedRef.current = next(startRef.current, dx, dy)
                    setDragArea(draggedRef.current)
                },
                onPanResponderRelease: release,
                onPanResponderTerminate: release,
            })
        const corners = Object.fromEntries(
            CORNERS.map((corner) => [corner, responder((start, dx, dy) => resizeCropArea(start, corner, dx, dy))])
        ) as Record<CropCorner, PanResponderInstance>
        return { move: responder(moveCropArea), corners }
    }, [])

    const shown = dragArea ?? area
    const x = clamp(shown.x)
    const y = clamp(shown.y)
    const right = clamp(shown.x + shown.width)
    const bottom = clamp(shown.y + shown.height)

    return (
        <View style={styles.root(maxWidth)} testID={testID} onLayout={onLayout}>
            {/* La photo et son voile sont rognés à ses coins arrondis ; le cadre, posé par-dessus, ne l'est pas :
                ses poignées débordent de la photo et se prennent entières au bord. */}
            <View style={styles.photo}>
                <FittedImage uri={uri} accessibilityLabel="Photo à recadrer" onRatio={setRatio} />
                {/* Le voile autour de la zone : quatre bandes, la zone reste nette. */}
                <View pointerEvents="none" style={styles.veil(0, 0, 1, y)} />
                <View pointerEvents="none" style={styles.veil(0, bottom, 1, 1)} />
                <View pointerEvents="none" style={styles.veil(0, y, x, bottom)} />
                <View pointerEvents="none" style={styles.veil(right, y, 1, bottom)} />
            </View>
            <View
                pointerEvents={interactive ? "auto" : "none"}
                style={[styles.frame(x, y, right, bottom), interactive && styles.grab]}
                accessibilityLabel={interactive ? "Zone retenue : glisse-la, ou tire un coin" : undefined}
                {...(interactive ? responders.move.panHandlers : {})}
            >
                {CORNERS.map((corner) => (
                    <View
                        key={corner}
                        style={[styles.hit, styles[corner], interactive && styles.resize(corner)]}
                        {...(interactive ? responders.corners[corner].panHandlers : {})}
                    >
                        <View style={styles.handle} />
                    </View>
                ))}
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /**
     * La boîte EST la photo (`<FittedImage>` en prend la proportion) : la zone, en fractions de la
     * boîte, tombe donc sur la photo. Bornée en largeur, et par la hauteur de la fenêtre (`maxWidth`) :
     * une page en portrait tient à l'écran, ses quatre coins compris.
     */
    root: (maxWidth: number) => ({
        width: "100%" as const,
        maxWidth,
        alignSelf: "center" as const,
    }),
    photo: {
        overflow: "hidden",
        borderRadius: theme.radius.md,
    },
    veil: (left: number, top: number, right: number, bottom: number) => ({
        ...box(left, top, right, bottom),
        backgroundColor: theme.colors.scrim,
    }),
    frame: (left: number, top: number, right: number, bottom: number) => ({
        ...box(left, top, right, bottom),
        borderWidth: 2,
        borderColor: theme.colors.role,
    }),
    /** La zone où le geste prend la poignée, centrée sur son coin ; la poignée dessinée au milieu. */
    hit: {
        position: "absolute",
        width: HANDLE_HIT,
        height: HANDLE_HIT,
        alignItems: "center",
        justifyContent: "center",
    },
    handle: {
        width: HANDLE,
        height: HANDLE,
        borderRadius: theme.radius.sm / 2,
        backgroundColor: theme.colors.role,
        borderWidth: 2,
        borderColor: theme.colors.white,
    },
    /**
     * Au web, le pointeur dit ce que fait le geste, et le navigateur ne défile pas la page sous lui
     * (`touchAction`) — le même réglage que le rangement d'une activité (`ActivitiesSection`).
     */
    grab: Platform.OS === "web" ? ({ cursor: "move", touchAction: "none" } as object) : {},
    resize: (corner: CropCorner) =>
        Platform.OS === "web"
            ? ({
                  cursor: corner === "topLeft" || corner === "bottomRight" ? "nwse-resize" : "nesw-resize",
                  touchAction: "none",
              } as object)
            : {},
    topLeft: { left: -HANDLE_HIT / 2, top: -HANDLE_HIT / 2 },
    topRight: { right: -HANDLE_HIT / 2, top: -HANDLE_HIT / 2 },
    bottomLeft: { left: -HANDLE_HIT / 2, bottom: -HANDLE_HIT / 2 },
    bottomRight: { right: -HANDLE_HIT / 2, bottom: -HANDLE_HIT / 2 },
}))
